import { zodResolver } from '@hookform/resolvers/zod';
import { Pencil } from 'lucide-react';
import { memo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { useTaxonomyContext } from '../../contexts/TaxonomyContext';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Textarea } from '@/components/ui/Textarea';
import i18n from '@/lib/i18n';
import { capitalize } from '@/lib/utils';
import { TaxonomyGetResponse } from '@/services/TaxonomiesService';

interface DataItemProps {
  propertiesOriginal: TaxonomyGetResponse[];
  propertyItem: TaxonomyGetResponse;
  attributesKeyTexts: Record<string, string>;
}

const taxonomyFormSchema = z.object({
  examples: z
    .string()
    .trim()
    .min(
      1,
      i18n.t(
        'pages.Taxonomy.components.TaxonomyList.DataItem.validation.examplesRequired',
      ),
    ),
});

type TaxonomyFormData = z.infer<typeof taxonomyFormSchema>;

export const DataItem = memo(function DataItem({
  propertiesOriginal,
  propertyItem,
  attributesKeyTexts,
}: DataItemProps) {
  const {
    taxonomyPropertiesUpdated,
    setTaxonomyPropertiesUpdated,
    selectedVersion,
  } = useTaxonomyContext();

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Taxonomy.components.TaxonomyList.DataItem',
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const taxonomyProperty = capitalize(
    propertyItem.property.replaceAll('_', ' '),
  );

  const updatedItem = taxonomyPropertiesUpdated?.data?.find(
    (item) => item.id === propertyItem.id,
  );

  const displayedExamples = updatedItem?.examples?.trim()
    ? updatedItem.examples
    : propertyItem.examples;

  const {
    formState: { errors, isSubmitting },
    register,
    handleSubmit,
    reset,
  } = useForm<TaxonomyFormData>({
    resolver: zodResolver(taxonomyFormSchema),
    defaultValues: {
      examples: displayedExamples,
    },
    mode: 'onSubmit',
  });

  function handleOpenModal() {
    const latestItem = taxonomyPropertiesUpdated?.data?.find(
      (item) => item.id === propertyItem.id,
    );

    reset({
      examples: latestItem?.examples || propertyItem.examples,
    });

    setIsModalOpen(true);
  }

  function handleCloseModal() {
    setIsModalOpen(false);
  }

  function onSubmit({ examples }: TaxonomyFormData) {
    setIsModalOpen(false);
    try {
      const propertyItemOriginal = propertiesOriginal.find(
        (item) => item.id === propertyItem.id,
      );

      const isSameAsOriginal =
        examples.trim() === propertyItemOriginal?.examples?.trim();

      const updatedData =
        taxonomyPropertiesUpdated?.data?.filter(
          (item) => item.id !== propertyItem.id,
        ) ?? [];

      const newData = isSameAsOriginal
        ? updatedData
        : [
            ...updatedData,
            {
              id: propertyItem.id ?? '',
              examples,
            },
          ];

      setTaxonomyPropertiesUpdated({
        version: selectedVersion,
        data: newData,
      });
      if (isSameAsOriginal) return;

      toast.success(t('toast.success'));
    } catch {
      toast.error(t('toast.error'));
    }
  }

  return (
    <div className="flex items-start gap-2 border-b border-solid border-[#C3C3C3] px-5 py-3">
      <dl className="w-[200px] text-sm font-bold">
        {attributesKeyTexts[propertyItem.type]}
      </dl>
      <dt className="w-[200px] text-sm font-bold">{taxonomyProperty}</dt>
      <dd className="flex-1 text-sm">{displayedExamples}</dd>

      <Button
        variant="ghost"
        className="p-0 hover:bg-transparent"
        onClick={handleOpenModal}
      >
        <Pencil />
      </Button>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent onInteractOutside={handleCloseModal}>
          <DialogHeader>
            <DialogTitle>{taxonomyProperty}</DialogTitle>

            <DialogDescription className="sr-only" />
          </DialogHeader>

          <div className="space-y-2">
            <p className="text-sm">{t('dialog.description')}</p>

            <form key={displayedExamples} onSubmit={handleSubmit(onSubmit)}>
              <FormGroup
                error={{
                  message: errors.examples?.message,
                  font: 'sm',
                  align: 'center',
                }}
              >
                <Textarea className="h-[120px]" {...register('examples')} />
                <button id="submit-taxonomy-values" className="hidden">
                  {t('dialog.saveButton')}
                </button>
              </FormGroup>
            </form>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={handleCloseModal}>
              {t('dialog.cancelButton')}
            </Button>
            <Button asChild disabled={isSubmitting}>
              <label htmlFor="submit-taxonomy-values">
                {t('dialog.saveButton')}
              </label>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
